<script setup lang="ts">
  import { computed } from 'vue';
  import PosUiIcon from '@/components/pos-v2/ui/PosUiIcon.vue';
  import type { TNavBadge } from '@/domain/layout/app-nav';

  /**
   * One drawer row (plan §4.2): a link, or the button of a group without a
   * page. Top-level rows carry an icon; sub-page rows are indented.
   */
  const props = withDefaults(
    defineProps<{
      /** i18n key of the label. */
      titleKey: string;
      to?: string;
      icon?: string;
      level?: 0 | 1;
      active?: boolean;
      badge?: TNavBadge | null;
      /** Group button (no page): shows a chevron and reports `expanded`. */
      expandable?: boolean;
      expanded?: boolean;
    }>(),
    {
      to: undefined,
      icon: undefined,
      level: 0,
      active: false,
      badge: null,
      expandable: false,
      expanded: false,
    }
  );

  const emit = defineEmits<{ (e: 'toggle'): void }>();

  /** Plan names are brand names: not translated. */
  const PLAN_BADGE_TEXT: Record<Exclude<TNavBadge, 'new'>, string> = {
    plus: 'Plus',
    pro: 'Pro',
  };

  const rowClass = computed(() => [
    `level-${props.level}`,
    { 'is-active': props.active },
  ]);
</script>

<template>
  <router-link
    v-if="to"
    :to="to"
    class="v2-shell-nav-item"
    :class="rowClass"
    :aria-current="active ? 'page' : undefined"
  >
    <PosUiIcon v-if="icon" :name="icon" class="row-icon" />
    <span class="row-label">{{ $t(titleKey) }}</span>
    <span v-if="badge" class="row-badge" :class="`badge-${badge}`">{{
      badge === 'new' ? $t('whatsNew.chip') : PLAN_BADGE_TEXT[badge]
    }}</span>
  </router-link>
  <button
    v-else
    type="button"
    class="v2-shell-nav-item"
    :class="rowClass"
    :aria-expanded="expandable ? String(expanded) : undefined"
    @click="emit('toggle')"
  >
    <PosUiIcon v-if="icon" :name="icon" class="row-icon" />
    <span class="row-label">{{ $t(titleKey) }}</span>
    <span v-if="badge" class="row-badge" :class="`badge-${badge}`">{{
      badge === 'new' ? $t('whatsNew.chip') : PLAN_BADGE_TEXT[badge]
    }}</span>
    <PosUiIcon
      v-if="expandable"
      name="chevron-down"
      class="row-chevron"
      :class="{ 'is-expanded': expanded }"
    />
  </button>
</template>

<style scoped lang="scss">
  .v2-shell-nav-item {
    @apply tw-flex tw-min-h-11 tw-w-full tw-cursor-pointer tw-select-none tw-items-center tw-border-0 tw-bg-transparent tw-px-4 tw-text-left tw-text-sm tw-font-medium tw-text-neutral-800 tw-no-underline tw-transition-colors tw-duration-150;

    &:hover {
      @apply tw-bg-neutral-50;
    }

    &:focus-visible {
      @apply tw-outline-none tw-ring-2 tw-ring-inset tw-ring-orange-500/40;
    }

    .row-icon {
      @apply tw-mr-5 tw-shrink-0 tw-text-xl tw-text-neutral-500;
    }

    // Up to two lines: long labels next to a Plus/Pro badge stay readable.
    .row-label {
      @apply tw-min-w-0 tw-flex-1 tw-overflow-hidden tw-py-2 tw-leading-snug;
      display: -webkit-box;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 2;
    }

    .row-badge {
      @apply tw-ml-2 tw-shrink-0 tw-rounded-full tw-px-1.5 tw-py-0.5 tw-text-[10px] tw-font-bold tw-uppercase tw-leading-none tw-tracking-wide;

      &.badge-new {
        @apply tw-bg-orange-50 tw-text-orange-700;
      }

      &.badge-plus,
      &.badge-pro {
        @apply tw-bg-neutral-100 tw-text-neutral-600;
      }
    }

    .row-chevron {
      @apply tw-ml-2 tw-shrink-0 tw-text-xl tw-text-neutral-500 tw-transition-transform tw-duration-150;

      &.is-expanded {
        @apply tw-rotate-180;
      }
    }

    &.level-1 {
      @apply tw-pl-14 tw-font-normal tw-text-neutral-600;
    }

    // The sidebar is the only orange "you are here" (V2 guidelines, Shell).
    &.is-active {
      @apply tw-bg-orange-50 tw-text-neutral-900;
      box-shadow: inset 3px 0 0 rgb(var(--c-orange-600));

      .row-icon {
        @apply tw-text-neutral-800;
      }
    }
  }
</style>
